import React, { ReactNode, useState } from 'react'
import { FaChevronDown, FaChevronRight } from 'react-icons/fa'
import { cx } from '../cx'
import s from './styles.module.scss'

export type CollapsibleVariant = 'group' | 'sub' | 'panel' | 'flat'

export interface CollapsibleSectionProps {
    title: ReactNode
    /** Small grey summary shown next to the title (counts etc.). */
    meta?: ReactNode
    /** Controls rendered in the header row; clicks do not toggle the section. */
    actions?: ReactNode
    defaultOpen?: boolean
    /** Controlled mode. */
    open?: boolean
    onToggle?: (open: boolean) => void
    /**
     * group  - outer card with a 2px accent bar
     * sub    - nested grey panel
     * panel  - standalone white card (filters)
     * flat   - hairline divider + unboxed body (detail pages)
     */
    variant?: CollapsibleVariant
    /** Semantic accent for `group` sections (`bad` marks a destructive area). */
    tone?: 'default' | 'bad'
    /** Heading level: outer groups h3, subsections h4. */
    level?: 'h3' | 'h4'
    /** Hide the "Show / Hide" hint text. */
    hideHint?: boolean
    className?: string
    bodyClassName?: string
    children: ReactNode
}

/**
 * Whole-row toggle section. Children are only mounted while open, so anything
 * that fetches on mount is loaded lazily on expand.
 */
export const CollapsibleSection = ({
    title,
    meta,
    actions,
    defaultOpen = false,
    open: controlledOpen,
    onToggle,
    variant = 'sub',
    tone = 'default',
    level,
    hideHint = false,
    className,
    bodyClassName,
    children,
}: CollapsibleSectionProps) => {
    const [internalOpen, setInternalOpen] = useState(defaultOpen)
    const open = controlledOpen ?? internalOpen
    const Heading = level ?? (variant === 'group' ? 'h3' : 'h4')

    const toggle = () => {
        const next = !open
        if (controlledOpen == null) setInternalOpen(next)
        onToggle?.(next)
    }

    return (
        <div className={cx(s[variant], tone === 'bad' && s.toneBad, className)}>
            <div className={s.row}>
                <button type="button" className={s.header} onClick={toggle} aria-expanded={open}>
                    {open ? <FaChevronDown aria-hidden="true" /> : <FaChevronRight aria-hidden="true" />}
                    <Heading>{title}</Heading>
                    {meta != null && meta !== '' && <span className={s.meta}>{meta}</span>}
                    {!hideHint && <span className={s.hint}>{open ? 'Hide' : 'Show'}</span>}
                </button>
                {actions && <div className={s.actions}>{actions}</div>}
            </div>
            {open && <div className={cx(s.body, bodyClassName)}>{children}</div>}
        </div>
    )
}
